/**
 * Mappings used to feed ISO-3166 alpha-2 codes into `<cmt-flag>`. Two input
 * sources: the numeric dialing code from `/localization/v2/dialingcodes/cities`
 * (for the Country picker) and the language `abbreviation` string from
 * `/phoneservice/v3/languages` (for the Language picker).
 *
 * Unknown keys return `null`; `<cmt-flag>` renders a text-code chip in that
 * case so unsupported countries still show something identifiable.
 */

const DIALING_CODE_TO_ISO2: Record<string, string> = {
  '1': 'US',
  '7': 'RU',
  '20': 'EG',
  '27': 'ZA',
  '30': 'GR',
  '31': 'NL',
  '32': 'BE',
  '33': 'FR',
  '34': 'ES',
  '36': 'HU',
  '39': 'IT',
  '40': 'RO',
  '41': 'CH',
  '43': 'AT',
  '44': 'GB',
  '45': 'DK',
  '46': 'SE',
  '47': 'NO',
  '48': 'PL',
  '49': 'DE',
  '51': 'PE',
  '52': 'MX',
  '54': 'AR',
  '55': 'BR',
  '56': 'CL',
  '57': 'CO',
  '60': 'MY',
  '61': 'AU',
  '62': 'ID',
  '63': 'PH',
  '64': 'NZ',
  '65': 'SG',
  '66': 'TH',
  '81': 'JP',
  '82': 'KR',
  '86': 'CN',
  '90': 'TR',
  '91': 'IN',
  '212': 'MA',
  '351': 'PT',
  '352': 'LU',
  '353': 'IE',
  '354': 'IS',
  '358': 'FI',
  '359': 'BG',
  '420': 'CZ',
  '421': 'SK',
  '852': 'HK',
  '886': 'TW',
  '971': 'AE',
};

export function isoForDialingCode(dialingCode: string | null | undefined): string | null {
  if (!dialingCode) return null;
  return DIALING_CODE_TO_ISO2[dialingCode] ?? null;
}

/**
 * Resolve an ISO code from a phoneservice `vorwahl` (e.g. `+49`, `+49800`,
 * `+3902`, `+4144`). Strips the leading `+`, tries the full digits, then
 * progressively shorter prefixes — `+49800` falls back to `49` → DE.
 * Returns null for the ebuero-intern sentinel `+0000`.
 */
export function isoForVorwahl(vorwahl: string | null | undefined): string | null {
  if (!vorwahl) return null;
  const digits = vorwahl.replace(/^\+/, '');
  if (!digits || /^0+$/.test(digits)) return null;
  for (let len = digits.length; len >= 1; len--) {
    const hit = DIALING_CODE_TO_ISO2[digits.slice(0, len)];
    if (hit) return hit;
  }
  return null;
}

/**
 * Special-case remapping for the language endpoint's `abbreviation` values
 * that don't already match ISO-2. Everything else (DE, ES, FR, IT, JP, NL,
 * TH, …) passes through.
 */
const LANGUAGE_ABBR_OVERRIDES: Record<string, string> = {
  EN: 'GB',    // "English (UK)"
  DE_CH: 'CH', // "German (Switzerland)"
};

export function isoForLanguageAbbreviation(abbr: string | null | undefined): string | null {
  if (!abbr) return null;
  const upper = abbr.toUpperCase();
  return LANGUAGE_ABBR_OVERRIDES[upper] ?? upper;
}
